import { useNavigate } from "react-router-dom";
import { Globe, Calendar, ShieldCheck, ArrowRight } from "lucide-react";

const features = [
  {
    icon: Globe,
    title: "전 세계 한인 싱글",
    description: "북미·유럽·아시아 어디서든 한 자리에서 만남",
  },
  {
    icon: Calendar,
    title: "격주 정기 모임",
    description: "토요일 오전 · 목요일 저녁, 시간대별 선택",
  },
  {
    icon: ShieldCheck,
    title: "안전한 검증 만남",
    description: "정회원·서류인증 멤버만 입장하는 비공개 세션",
  },
];

export const GlobalZoomSection = () => {
  const navigate = useNavigate();

  return (
    <section
      id="global-zoom"
      className="pt-4 pb-10 lg:pt-6 lg:pb-14 bg-white relative overflow-hidden"
      data-testid="global-zoom-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-6">
          <span className="text-gold-500 font-medium text-sm tracking-wider uppercase mb-4 block">
            Global Zoom Meeting
          </span>
          <h2 className="font-sans text-3xl md:text-4xl font-[720] text-stone-800 mb-4">
            글로벌 줌미팅
          </h2>
          <p className="text-stone-500 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
            대륙과 시차를 가로질러, 진심이 마주 앉는 자리.<br />
            화면 너머 한 사람의 결을 가만히 읽어 내려가는 시간이 매주 열립니다.
          </p>
        </div>

        {/* Image Collage — asymmetric layout */}
        <div className="grid grid-cols-12 gap-3 mb-6 relative">
          {/* Large left image (7/12 columns, taller) */}
          <div className="col-span-12 md:col-span-7 relative rounded-3xl overflow-hidden group aspect-[16/10] md:aspect-auto md:min-h-[360px]">
            <img
              src="/images/zoom/zoom-1.png"
              alt="줌미팅 중인 여성"
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/55 via-black/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <span className="inline-block text-[10px] tracking-[0.2em] uppercase bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded-full border border-white/30 mb-2">
                Live · 라이브 만남
              </span>
              <h3 className="text-xl md:text-2xl font-bold leading-tight mb-1">
                화면 너머의 설렘
              </h3>
              <p className="text-white/85 text-xs md:text-sm">
                얼굴을 마주하며 시작되는 자연스러운 호감
              </p>
            </div>

            {/* Overlapping floating image (1:1 데이트) - clean, upright, fully visible */}
            <div className="hidden md:block absolute right-4 lg:right-6 bottom-4 lg:bottom-6 w-48 lg:w-64 aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white z-20 group-hover:scale-105 transition-transform duration-500" data-testid="zoom-overlap-image">
              <img
                src="/images/zoom/zoom-3.png"
                alt="1:1 줌 데이트"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent pt-6 pb-2 px-3">
                <span className="text-white text-[10px] tracking-[0.2em] uppercase font-semibold">
                  1:1 줌 데이트
                </span>
              </div>
            </div>
          </div>

          {/* Right column: smaller image stacked with caption card */}
          <div className="col-span-12 md:col-span-5 flex flex-col gap-3">
            <div className="relative rounded-3xl overflow-hidden group flex-1 aspect-[4/3] md:aspect-auto md:min-h-[220px]">
              <img
                src="/images/zoom/zoom-2.png"
                alt="줌미팅 중인 남성"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <span className="inline-block text-[10px] tracking-[0.2em] uppercase bg-burgundy-500/90 px-2.5 py-1 rounded-full mb-1.5">
                  1:1 매칭
                </span>
                <h3 className="text-lg font-bold leading-tight">
                  진솔한 대화
                </h3>
              </div>
            </div>
            {/* Stat / quote card */}
            <div className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-3xl p-5 flex items-center gap-4 border border-stone-200/70">
              <div className="flex-shrink-0">
                <p className="text-3xl font-[720] text-burgundy-500 leading-none">
                  98<span className="text-base text-stone-400 ml-0.5">%</span>
                </p>
                <p className="text-[10px] text-stone-500 tracking-wider uppercase mt-1">
                  재참여율
                </p>
              </div>
              <p className="text-xs md:text-sm text-stone-600 leading-relaxed italic">
                "오프라인보다 편하게,<br />그러나 깊이 있게 마주 앉는 시간"
              </p>
            </div>
          </div>
        </div>

        {/* Feature points */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {features.map((f, idx) => {
            const Icon = f.icon;
            return (
              <div
                key={idx}
                className="bg-stone-50 rounded-2xl p-4 border border-stone-200/60 hover:border-burgundy-300 hover:shadow-md transition-all flex items-start gap-3"
                data-testid={`global-zoom-feature-${idx}`}
              >
                <div className="w-9 h-9 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-stone-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-stone-800 font-bold text-sm mb-0.5">{f.title}</h3>
                  <p className="text-stone-500 text-xs leading-relaxed">{f.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigate("/global-zoom")}
            className="group inline-flex items-center gap-2 bg-burgundy-500 hover:bg-burgundy-600 text-white font-semibold text-sm px-7 py-3 rounded-full transition-all shadow-md"
            data-testid="global-zoom-cta-primary"
          >
            줌미팅 일정 보기
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
          <button
            onClick={() => navigate("/zoom-date")}
            className="inline-flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-medium px-6 py-3 rounded-full border border-stone-200 transition-all"
            data-testid="global-zoom-cta-secondary"
          >
            줌데이트 신청
          </button>
        </div>
      </div>
    </section>
  );
};
